import {
    Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalCloseButton, ModalFooter,
    Button, Text, Box, Image,
    Flex,
    Select
} from "@chakra-ui/react";
import { useRef, useState, ChangeEvent, useEffect } from "react";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import UploadLogo from '../../../../assets/images/UploadLogo.svg';
import { hiringCategoryList, IWorkModeDropdown } from "./modal";

interface BulkResumeUploadProps {
    resumeModalOpen: boolean;
    setResumeUploadModalOpen: (isOpen: boolean) => void;
    jobId: number;
    loadCandidateList: () => void;
}

const experince = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
]


export default function BulkResumeUpload({ resumeModalOpen, setResumeUploadModalOpen, jobId, loadCandidateList }: BulkResumeUploadProps) {

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [workModeList, setWorkModeList] = useState<IWorkModeDropdown[]>([]);
    const [selectedWorkMode, setSelectedWorkMode] = useState<string>("");
    const [selectedExperience, setSelectedExperience] = useState<string>("");
    const [selectedHiringCategory, setSelectedHiringCategory] = useState<string>("");

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList) {
            setFiles([]);
            return;
        }

        const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
        const selected = Array.from(fileList);

        // Filter only PDFs
        const nonPdfFiles = selected.filter(file => file.type !== "application/pdf");
        const pdfFiles = selected.filter(file => file.type === "application/pdf");

        if (nonPdfFiles.length > 0) {
            const names = nonPdfFiles.map(f => f.name).join(", ");
            addToast({
                message: `Only PDF files are allowed. Removed: ${names}`,
                status: "error",
            });
        }

        const tooLargeFiles = pdfFiles.filter(file => file.size > MAX_FILE_SIZE);
        const validFiles = pdfFiles.filter(file => file.size <= MAX_FILE_SIZE);

        if (tooLargeFiles.length > 0) {
            const fileNames = tooLargeFiles.map((f) => f.name).join(", ");
            addToast({
                message: `These files exceed 2 MB and were not added: ${fileNames}`,
                status: "error",
            });
        }
        setFiles(validFiles);
    };


    const uploadFiles = async () => {
        if (files.length === 0) {
            addToast({ message: "Please select resumes", status: "error" });
            return;
        }
        const formData = new FormData();
        formData.append("job_Id", jobId.toString());
        formData.append("work_Mode_Id", selectedWorkMode);
        formData.append("hiring_Category", selectedHiringCategory);
        formData.append("experience", selectedExperience);
        files.forEach((file) => {
            formData.append("resumes", file);
        });
        const response = await fetchApi("Candidate/bulkResumeUpload", "POST", formData, "", "", true);
        if (response) {
            addToast({ message: "Resumes uploaded successfully", status: "success" });
            setResumeUploadModalOpen(false);
            setFiles([]);
            loadCandidateList();
        }
    };

    const loadWorkMode = async () => {
        const response = await fetchApi("RecruitmentMaster/WorkMood", "GET", "", "", "");
        if (response) {
            setWorkModeList(response);
        }
    }

    useEffect(() => {
        loadWorkMode();
    }, [])

    return (
        <Modal isOpen={resumeModalOpen} onClose={() => { setResumeUploadModalOpen(false); setFiles([]); }} size="3xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader><Text align={"center"}>Bulk Resume Upload</Text></ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Box w="100%" display="flex" flexDir="column" alignItems="center" justifyContent="center" py={2}>
                        <Flex gap={4} w="100%" mb={5}>
                            <Box w="50%">
                                <Text fontSize="sm" mb={1} fontWeight="500">Work Mode <Text as="span" color="red">*</Text></Text>
                                <Select placeholder="Select Work Mode" onChange={(e) => setSelectedWorkMode(e.target.value)}>
                                    {workModeList.map((w) => (
                                        <option key={w.id} value={w.id}>{w.work_Mood}</option>
                                    ))}
                                </Select>
                            </Box>

                            <Box w="50%">
                                <Text fontSize="sm" mb={1} fontWeight="500">Hiring Category <Text as="span" color="red">*</Text></Text>
                                <Select placeholder="Select Hiring Category" onChange={(e) => setSelectedHiringCategory(e.target.value)}>
                                    {hiringCategoryList.map((w) => (
                                        <option key={w.id} value={w.id}>{w.category}</option>
                                    ))}
                                </Select>
                            </Box>

                            <Box w="50%">
                                <Text fontSize="sm" mb={1} fontWeight="500">Experience <Text as="span" color="red">*</Text></Text>
                                <Select placeholder="Select Experience" onChange={(e) => setSelectedExperience(e.target.value)}>
                                    {experince.map((exp) => (
                                        <option key={exp.value} value={exp.value}>{exp.label}</option>
                                    ))}
                                </Select>
                            </Box>
                        </Flex>

                        <input type="file" multiple accept="application/pdf" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                        <Box border="2px dashed #8F8F8F" borderRadius="12px" p={8} w="100%" textAlign="center" cursor="pointer" onClick={() => fileInputRef?.current?.click()} >
                            <Image src={UploadLogo} mx="auto" mb={3} w="50px" />
                            <Text className="font-poppins text_md text_medium" mb={2}>
                                Click to select resumes
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                (Only PDF & max 2 MB per file)
                            </Text>
                        </Box>

                        {files.length > 0 && (
                            <Box w="100%" mt={5} p={3} border="1px solid #e2e2e2" borderRadius="md">
                                <Text fontWeight="600" mb={2}>Selected Files:</Text>
                                {files.map((f, i) => (
                                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
                                        <Text fontSize="sm">{f.name}</Text>
                                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} >
                                            Remove
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </ModalBody>

                <ModalFooter>
                    <Button colorScheme="green" isLoading={loading} onClick={uploadFiles}>
                        Upload
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
